import { ApiClient } from '@/lib/api'
import { AI_API_URL } from '@/lib/constants'

import { API_PATHS } from './constants'
import { assembleSessionEvents } from './utils/assemble-session-events'

import type { AIModel } from './constants'
import type {
	SessionDetailResponse,
	SessionEventsResponse,
	SessionListResponse,
} from './types'

/**
 * Dedicated API client pointed at the Noah backend (`VITE_AI_API_URL`). Reuses
 * the shared `ApiClient` so Clerk auth, tenant headers, Sentry trace + 401
 * retry behavior match the rest of the app.
 */
const api = new ApiClient(AI_API_URL)

/**
 * List Noah chat sessions for a tenant. Newest-updated first; backend caps at 50.
 */
export const listSessions = async (
	tenantId: string,
	signal?: AbortSignal,
): Promise<SessionListResponse> => {
	const { data } = await api.get<SessionListResponse>(API_PATHS.sessions, {
		params: { tenant_id: tenantId },
		signal,
	})
	return data
}

/**
 * Fetch a single session. Backend ships the persisted UI message stream as
 * `events: UIMessageChunk[]` — the same shape that flows live over
 * `/api/chat`. `assembleSessionEvents` folds those events back into the
 * `UIMessage[]` that `useChat({ messages })` consumes.
 *
 * Endpoint picks model: `'beta'` → `/api/beta/sessions/{id}`,
 * `'standard'` → `/api/sessions/{id}`. Both backends will eventually return
 * the same events shape; during the beta phase the MSW handler serves both
 * with events bodies so the FE works end-to-end before the standard port
 * lands.
 *
 * @param sessionId Backend session id to fetch
 * @param tenantId Current tenant scope
 * @param model Which endpoint pair to hit (`'standard'` or `'beta'`)
 * @param signal Abort signal from the query context — cancels on unmount/refetch
 */
export const getSession = async (
	sessionId: string,
	tenantId: string,
	model: AIModel,
	signal?: AbortSignal,
): Promise<SessionDetailResponse> => {
	const path =
		model === 'beta'
			? API_PATHS.betaSession(sessionId)
			: API_PATHS.session(sessionId)
	// Cutover window: the standard endpoint still returns the legacy
	// `{ messages: UIMessage[] }` shape, while the beta endpoint already
	// emits `{ events: UIMessageChunk[] }`. Detect which we got and only
	// run the assembler on the events shape. Once Austin's port lands on
	// `/api/sessions/:id` this branch collapses to the events path.
	const { data } = await api.get<SessionEventsResponse | SessionDetailResponse>(
		path,
		{
			params: { tenant_id: tenantId },
			signal,
		},
	)

	const messages = Array.isArray((data as SessionEventsResponse).events)
		? await assembleSessionEvents((data as SessionEventsResponse).events)
		: ((data as SessionDetailResponse).messages ?? [])

	return {
		session_id: data.session_id,
		title: data.title,
		created_at: data.created_at,
		updated_at: data.updated_at,
		messages,
	}
}

/**
 * Rename a session. Body accepts `title` only; backend strips whitespace and
 * clamps to 255 chars. Returns the updated `{ session_id, title }`.
 *
 * Per CLAUDE.md "Abort Signal Convention", PATCH does not accept a signal —
 * cancellation causes silent UI failures.
 */
export const patchSession = async (
	sessionId: string,
	tenantId: string,
	title: string,
): Promise<{ session_id: string; title: string }> => {
	const { data } = await api.patch<{ session_id: string; title: string }>(
		API_PATHS.session(sessionId),
		{ title },
		{ params: { tenant_id: tenantId } },
	)
	return data
}

/**
 * Delete a Noah chat session. Idempotent on the backend; no AbortSignal is
 * passed per the project convention (POST/DELETE cancellation causes silent UI
 * failures — CLAUDE.md "Abort Signal Convention").
 */
export const deleteSession = async (
	sessionId: string,
	tenantId: string,
): Promise<{ ok: true }> => {
	const { data } = await api.delete<{ ok: true }>(
		API_PATHS.session(sessionId),
		{
			params: { tenant_id: tenantId },
		},
	)
	return data
}
